import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-mfa',
  templateUrl: './mfa.component.html',
  styleUrls: ['./mfa.component.scss'],
})
export class MfaComponent implements OnInit {
  @ViewChild('tooltip') tooltip: NgbTooltip;

  mfaRequestReason = '';
  redactedEmail = '';
  mfaCode = '';
  errorMessage = '';
  successMessage = '';
  isRequestButtonDisabled = true; // Initially disabled
  secondsBetweenRequests = 30;
  countdown = 30;
  redirectCountdown = 5;
  username = '';
  deviceFingerprint = '';
  ipAddress = '';
  country = '';
  region = '';
  city = '';
  failedRequestCount = 0;
  failedRequestLimit = 3;
  showRedirect = false;
  deviceName = ''; // Device name input from the user
  trustDeviceAndLocation = false; // Checkbox for trusting device and location

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.showRedirect = false;

    // Fetch query parameters
    this.username = this.route.snapshot.queryParamMap.get('username') || '';
    this.mfaRequestReason = this.route.snapshot.queryParamMap.get('mfaRequestReason') || '';
    this.deviceFingerprint = this.route.snapshot.queryParamMap.get('deviceFingerprint') || '';
    this.ipAddress = this.route.snapshot.queryParamMap.get('ipAddress') || '';
    this.redactedEmail = this.route.snapshot.queryParamMap.get('email') || '';
    this.country = this.route.snapshot.queryParamMap.get('country') || '';
    this.region = this.route.snapshot.queryParamMap.get('region') || '';
    this.city = this.route.snapshot.queryParamMap.get('city') || '';

    // Fetch MFA configuration after username is set
    this.fetchMfaConfiguration();
  }

  fetchMfaConfiguration(): void {
    if (!this.username) {
      console.error('Username is not available for fetching MFA configuration.');
      return;
    }

    this.authenticationService.getMfaConfiguration(this.username).subscribe({
      next: (config) => {
        this.failedRequestLimit = config.failedAttemptsLimit;
        this.secondsBetweenRequests = config.secondsBetweenRequests;

        // Start the countdown for disabling the request button
        this.startCountdown();
      },
      error: () => {
        console.error('Failed to fetch MFA configuration. Using default values.');
        this.startCountdown(); // Start countdown with default values
      },
    });
  }

  onSubmit(): void {
    this.authenticationService
      .verifyMfaCode(
        this.username,
        this.ipAddress,
        this.deviceFingerprint,
        this.deviceName,
        this.mfaRequestReason,
        this.country,
        this.region,
        this.city,
        this.mfaCode,
        this.trustDeviceAndLocation
      )
      .subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.successMessage = 'Authorisation successful!';
            const authenticated = this.authenticationService.authenticate(
              response.user,
              response.userData,
              response.token,
              response.tokenExpirationDate
            );
            if (authenticated) {
              this.router.navigate(['/pcn-details']);
            }
            // this.startRedirectCountdown();
          } else {
            this.errorMessage = response.message;
            this.failedRequestCount++;

            if (this.failedRequestCount >= this.failedRequestLimit) {
              this.authenticationService.lockAccount(this.username).subscribe({
                next: (response2) => {
                  if (response2.result) {
                    this.errorMessage =
                      'There have been too many failed authorisation attempts. Your account has now been locked. Please contact your manager to unlock your account.';
                    this.startRedirectCountdown();
                  }
                },
              });
            }
          }
        },
        error: () => {
          this.errorMessage = 'An error occurred. Please try again later.';
        },
      });
  }

  requestMfaCode(): void {
    this.isRequestButtonDisabled = true;
    this.authenticationService
      .requestMfaCode(this.username, this.ipAddress, this.deviceFingerprint, this.mfaRequestReason)
      .subscribe({
        next: () => {
          this.startCountdown();
        },
        error: () => {
          this.errorMessage = 'Failed to request a new authorisation code. Please try again later.';
          this.isRequestButtonDisabled = false;
        },
      });
  }

  startCountdown(): void {
    this.isRequestButtonDisabled = true;
    this.countdown = this.secondsBetweenRequests;
    const interval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(interval);
        this.isRequestButtonDisabled = false;
      }
    }, 1000);
  }

  startRedirectCountdown(): void {
    this.showRedirect = true;
    const interval = setInterval(() => {
      this.redirectCountdown--;
      if (this.redirectCountdown <= 0) {
        clearInterval(interval);
        this.router.navigate(['/login']);
      }
    }, 1000);
  }
}
